import { createAccount } from 'Api/AccountApi';
import { trackEvent, TRACKING_ENUMS } from 'Utils/Analytics';
import { ON_SIGNUP_ERROR_MESSAGE, GIFTING_QUERY_PARAM } from 'Utils/Constants';
import { onSubmitReactFormError } from 'Utils/FormValidation';
import { hasEntitlementCookie } from 'Utils/Entitlements';
import { getSheerVerificationId } from 'Utils/SheerIdHelpers';
import { isDefaultPlanFlow } from './Analytics/Amplitude';
import { getMemberVerify } from 'Api/SheerIdService';
import { getStoreState } from 'Utils/Store';
import { isSheerProductSKU, getProductSKUSheerType } from 'Utils/SheerIdHelpers';
import { getTokenExpirations, isNonAnonymousUser } from './UserCookie';

export const createUserAccount = async (values, onSignInSuccess, onSignUpSuccess, analyticTraits) => {
  const res = await createAccount(values);
  const { accessToken, refreshToken, tokenExpiration, cpCustomerID, userType } = res;
  const expirations = getTokenExpirations(tokenExpiration);

  const { firstName = '', lastName = '', email } = values;
  const userData = {
    ...res,
    firstName,
    lastName,
    email,
    accessToken,
    refreshToken,
    ...expirations,
    profileId: cpCustomerID,
  };

  if (accessToken) {
    await onSignInSuccess(userData, true, true);
    const utagData = {
      profile_type: userType,
      newsletter_opt_in: 'yes',
      dcg_profile_id: cpCustomerID,
    };

    trackEvent(TRACKING_ENUMS.EVENT_SIGNUP_COMPLETED, { ...analyticTraits }, utagData);

    return onSignUpSuccess(userData);
  }

  const { failureMessage } = res?.response?.data;
  const errorDisplayText = ON_SIGNUP_ERROR_MESSAGE.onVerificationError;
  const errorCode = (failureMessage && failureMessage.errorCode) || 'server-side';
  const errorMsg = (failureMessage && failureMessage.errorMessage) || errorDisplayText;

  trackEvent(
    TRACKING_ENUMS.EVENT_SIGNUP_ERROR,
    {
      page_error_type: errorCode,
      page_error_description: errorMsg,
      ...analyticTraits,
    },
    {
      dcg_profile_id: cpCustomerID,
      error_type: errorCode,
      error_message: errorMsg,
    }
  );
  return onSubmitReactFormError(errorMsg, errorCode);
};

export const signInRedirect = async (entitlementFromSignIn, location) => {
  const {
    appConfig: { defaultConfig, routes },
    purchaseMode: { requirePlanSelection, isGifting = false },
    secondScreen: { isDeviceActivation },
    subscription,
    integrations: { bango },
  } = getStoreState();

  let isExternalRedirect, redirectTo;

  const { rootDomain, hideHasSubScreenOnSignUp, alwaysRefreshOnRootSignin } = defaultConfig;

  const search = isGifting ? GIFTING_QUERY_PARAM : '';
  // set default path
  let path = '/';
  let step = '';
  let redirectUrl = '';

  // if return_to param is passed to the page
  const query = new URLSearchParams(location.search);
  const returnUrl = query.get('return_to');
  const hasEntitlement = hasEntitlementCookie() || entitlementFromSignIn;

  if (isGifting) {
    redirectTo = routes.giftingReviewAndRedeem;
  }

  if (isDeviceActivation) {
    if (!hasEntitlement) {
      redirectTo = routes.signupPlans;
    } else {
      redirectTo = routes.secondScreenSuccess;
      isExternalRedirect = true;
    }
  }

  if (returnUrl) {
    isExternalRedirect = !!returnUrl.match(/http/i);
    redirectTo = returnUrl;
  }

  // if redirect has specific path
  if (redirectTo) {
    path = redirectTo;
    if (hasEntitlement && !isDeviceActivation && !isGifting) {
      path = rootDomain;
      if (hideHasSubScreenOnSignUp || !!path.match(/http/i)) {
        isExternalRedirect = true;
      }
    }
  } else {
    const { from } = location.state && location.state.from ? location.state : { from: { pathname: '/' } };
    const isRootUrl = from.pathname === '/';
    if (isRootUrl && !hasEntitlement && requirePlanSelection) {
      path = routes.signupPlans;
    } else if (isRootUrl && document.referrer !== '') {
      path = rootDomain;
      isExternalRedirect = true;
    } else {
      path = from.pathname;
    }
  }

  // check if alwaysRefreshOnRootSignin for d2c products only
  if (path === '/' && alwaysRefreshOnRootSignin) {
    isExternalRedirect = true;
  }
  if (isDeviceActivation && isExternalRedirect) {
    window.location.replace(path);
    return {};
  }

  if (hasEntitlement && returnUrl && returnUrl.includes(routes.signupCreateAccount)) {
    if (subscription.isIAPUser && subscription.hasActiveSubscription && getSheerVerificationId()) {
      path = routes.signupCurrentSubscriberMilitaryFirst;
    } else {
      path = returnUrl;
    }
  }

  if (isDefaultPlanFlow() && !subscription.hasActiveSubscription && !returnUrl) {
    path = routes.payment;
  }

  if (bango.subscriptionId) {
    path = routes.bangoFinal;
    if (isSheerProductSKU(bango.productId)) {
      const sheerType = getProductSKUSheerType(bangoProductId);
      path = sheerType ? `${routes.sheerIdVerification}?sheerVerification=${sheerType}` : routes.sheerIdVerification;
    }
  }

  if (isNonAnonymousUser() && (subscription.isNewUser || subscription.isChurnedUser)) {
    try {
      const response = await getMemberVerify();
      if (response?.verificationId) {
        path = routes.sheerIdVerification;
        if (response?.segment) path = `${routes.sheerIdVerification}?sheerVerification=${response.segment}`;
        step = response?.currentStep;
        redirectUrl = response?.bangoRedirectUrl ?? '';
      }
    } catch (error) {
      if (error?.verificationId) {
        path = routes.sheerIdVerification;
        if (error?.segment) path = `${routes.sheerIdVerification}?sheerVerification=${error.segment}`;
        step = error?.currentStep;
        redirectUrl = error?.bangoRedirectUrl ?? '';
      }
    }
  }

  if (isExternalRedirect) {
    window.location.replace(path);
    return { showRedirectLoading: true };
  } else {
    return {
      redirectPath: path,
      redirectSearch: search,
      redirectStep: step,
      redirectUrl,
    };
  }
  // return { redirectPath, redirectSearch, showRedirectLoading };
};
